<template>
  <div>
    <multiselect
      class="multiselect-appointment"
      v-model="selectedAppointment"
      :options="filteredAppointments"
      :placeholder="resolvedPlaceholder"
      :allow-empty="resolvedAllowEmpty"
      track-by="number"
      :deselectLabel="$t('deselectLabel')"
      selectLabel=""
      selectedLabel=""
      :internal-search="false"
      :show-labels="true"
      @open="onAppointmentDropdownOpen"
      @search-change="onAppointmentSearch"
    >
      <template slot="singleLabel" slot-scope="{ option }">
        <div v-if="option">
          #{{ option.number.toString().padStart(4, '0') }}
          <span v-if="option.installationStartAt">
            - {{ formatAppointmentDateTime(option.installationStartAt) }}
          </span>
          <span v-if="option.appointmentType">- {{ $t(option.appointmentType) }}</span>
        </div>
      </template>
      <template slot="option" slot-scope="{ option }">
        <div>
          #{{ option.number.toString().padStart(4, '0') }}
          <span v-if="option.project && option.project.customer">
            - {{ option.project.customer.lastname }}, {{ option.project.customer.firstname }}
          </span>
        </div>
        <div v-if="option.installationStartAt">
          {{ formatAppointmentDateTime(option.installationStartAt) }}
          <span v-if="option.installationEndAt">
            - {{ formatAppointmentTime(option.installationEndAt) }}
          </span>
        </div>
        <div v-if="option.appointmentType">
          {{ $t(option.appointmentType) }}
        </div>
      </template>
      <template slot="noResult">
        <div>{{ $t('no_results') }}</div>
      </template>
    </multiselect>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';

const createAppointmentsFilter = () => {
  return {
    pagination: {
      page: 0,
      rowsPerPage: 50,
    },
    sortField: 'installationStartAt',
    sortOrder: -1,
    filters: {
      project: { value: null },
      projectCustomerSearch: { value: null },
    },
  };
};

export default {
  components: {
    Multiselect,
  },
  props: {
    appointment: {
      type: Object,
      default: null,
    },
    projectContext: {
      type: Object,
      default: null,
    },
    allowEmpty: {
      type: Boolean,
      default: true,
    },
    preventDeselect: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: null,
    },
  },
  model: {
    prop: 'appointment',
    event: 'select',
  },
  data() {
    return {
      filteredAppointments: [],
      appointmentsFilter: createAppointmentsFilter(),
    };
  },
  computed: {
    ...mapGetters(['getAppointments']),
    selectedAppointment: {
      get() {
        return this.appointment;
      },
      set(appointment) {
        this.$emit('select', appointment);
      },
    },
    resolvedPlaceholder() {
      return this.placeholder || this.$t('search');
    },
    resolvedAllowEmpty() {
      if (this.preventDeselect && this.appointment) {
        return false;
      }

      return this.allowEmpty;
    },
  },
  methods: {
    ...mapActions(['fetchAppointmentPages']),
    formatAppointmentDateTime(value) {
      return this.$dayjs(value).format('DD.MM.YYYY HH:mm');
    },
    formatAppointmentTime(value) {
      return this.$dayjs(value).format('HH:mm');
    },
    ensureSelectedAppointmentIsAvailable() {
      if (!this.appointment) {
        return;
      }

      const hasSelectedAppointment = this.filteredAppointments.some(
        (filteredAppointment) => filteredAppointment.number === this.appointment.number
      );

      if (!hasSelectedAppointment) {
        this.filteredAppointments = [this.appointment, ...this.filteredAppointments];
      }
    },
    async onAppointmentSearch(rawQuery) {
      const query = rawQuery ? rawQuery.toLowerCase() : null;
      this.appointmentsFilter.filters.projectCustomerSearch.value = query;
      await this.loadAppointmentData();
    },
    async onAppointmentDropdownOpen() {
      await this.loadAppointmentData();
    },
    async loadAppointmentData() {
      this.appointmentsFilter.filters.project.value = this.projectContext?.id || null;

      await this.fetchAppointmentPages({
        page: this.appointmentsFilter.pagination.page,
        pageSize: this.appointmentsFilter.pagination.rowsPerPage,
        sortField: this.appointmentsFilter.sortField,
        sortOrder: this.appointmentsFilter.sortOrder,
        filters: { ...this.appointmentsFilter.filters },
      });

      this.filteredAppointments = [...this.getAppointments];
      this.ensureSelectedAppointmentIsAvailable();
    },
  },
  watch: {
    appointment() {
      this.ensureSelectedAppointmentIsAvailable();
    },
    projectContext() {
      this.filteredAppointments = [];
    },
  },
};
</script>

<style scoped lang="scss">
.multiselect__content-wrapper {
  position: absolute;
}

.multiselect-appointment {
  position: relative;
}
</style>
